/**
 * 管端协同编辑的消息格式（客户端 web/src/lib/collab.ts 与房间 collab-room.ts 共用）。纯函数，有用例。
 *
 * 全部代理共用一个房间，代理与版本号带在消息里：顶栏看得见我有权限的所有代理下在线的人（点头像可以跨代理跟过去），
 * 鼠标 / 描格 / 「存了」只对同一代理同一版本的人生效。只能看一个代理的账号，房间只给它发那个代理里的人（canSee）。
 * 上行：state（我在哪个版本哪页、在填哪格、鼠标在哪、选中哪关、滚到哪、空闲多久、页面是不是在后台）/ saved（我刚把第几版存到第几号、改了哪儿）
 * 下行：hello（你的 id + 房间里已有的人）/ peer（某人的最新状态）/ leave / saved
 */
export interface CollabPeer {
  id: string;
  name: string;
  /** 在哪个代理（A0）。没定下来时为空串 */
  a0: string;
  page: string;
  /** 在填哪一格：页面内第几个输入控件 + 它的标签（给别人的提示用） */
  field: { idx: number; label: string } | null;
  /** 鼠标在内容区里的相对位置（0~1，按内容区整体尺寸，含滚动） */
  cursor: { x: number; y: number } | null;
  /** 在看哪个配置版本（不在版本化页面上为 null）。鼠标与描格只在同版本同页时画 */
  version: number | null;
  /** 选中的关卡（uid）：别人点头像跳过来时选同一关 */
  selUid: string | null;
  /** 页面滚到哪（0~1）：跳过来时滚到同一处 */
  scroll: number | null;
  /** 空闲了多少分钟（5 分钟没动起算），没空闲为 null */
  idleMin: number | null;
  /** 页面在后台（切走了标签页、窗口被别的程序挡住）：人还在，只是没在看——头像变灰、不画鼠标 */
  away: boolean;
}
export type CollabUp =
  | { t: "state"; page: string; field: CollabPeer["field"]; cursor: CollabPeer["cursor"]; version: number | null; selUid: string | null; scroll: number | null; idleMin: number | null; away: boolean; a0: string | null }
  | { t: "saved"; revision: number; summary: string; version: number | null; a0?: string };

const str = (v: unknown, max: number) => (typeof v === "string" ? v.slice(0, max) : null);
const unit = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : null);

/** 上行消息校验：不认识的、形状不对的一律丢掉（房间只转发，不能让坏消息广播给所有人） */
export function parseCollabMessage(raw: string): CollabUp | null {
  let m: Record<string, unknown>;
  try { m = JSON.parse(raw); } catch { return null; }
  if (!m || typeof m !== "object") return null;
  if (m.t === "state") {
    const page = str(m.page, 64) ?? "";
    let field: CollabPeer["field"] = null;
    const f = m.field as Record<string, unknown> | null;
    if (f && typeof f === "object" && Number.isInteger(f.idx) && (f.idx as number) >= 0 && (f.idx as number) < 10000) field = { idx: f.idx as number, label: str(f.label, 40) ?? "" };
    let cursor: CollabPeer["cursor"] = null;
    const c = m.cursor as Record<string, unknown> | null;
    if (c && typeof c === "object") { const x = unit(c.x), y = unit(c.y); if (x != null && y != null) cursor = { x, y }; }
    const version = Number.isSafeInteger(m.version) && (m.version as number) > 0 ? (m.version as number) : null;
    const selUid = str(m.selUid, 40);
    const scroll = unit(m.scroll);
    const idleMin = Number.isSafeInteger(m.idleMin) && (m.idleMin as number) >= 0 && (m.idleMin as number) < 100000 ? (m.idleMin as number) : null;
    const a0 = str(m.a0, 32);
    return { t: "state", page, field, cursor: m.away === true ? null : cursor, version, selUid: selUid || null, scroll, idleMin, away: m.away === true, a0: a0 ? a0.trim() || null : null };
  }
  if (m.t === "saved") {
    if (!Number.isSafeInteger(m.revision) || (m.revision as number) < 0) return null;
    const version = Number.isSafeInteger(m.version) && (m.version as number) > 0 ? (m.version as number) : null;
    return { t: "saved", revision: m.revision as number, summary: str(m.summary, 120) ?? "", version };
  }
  return null;
}

/**
 * 这条连接能不能看见某个代理里的人。scope 是连接的权限：`*` = 票里没绑代理（超管，能切任意代理），
 * 否则是票里绑定的那一个代理。没定代理的人（a0 为空）只给 `*` 看
 */
export function canSee(scope: string, a0: string): boolean {
  return scope === "*" || (a0 !== "" && scope === a0);
}

/** 人 → 颜色：按名字哈希到 8 色，同一个人在谁的屏幕上都是同一个颜色 */
export const COLLAB_COLORS = ["#e8590c", "#1c7ed6", "#2f9e44", "#ae3ec9", "#f08c00", "#0c8599", "#e03131", "#5f3dc4"];
export function colorOf(name: string): string {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return COLLAB_COLORS[h % COLLAB_COLORS.length];
}

/** 每条连接的限流计数：1 秒窗口内的状态条数、上一次「存了」的时刻 */
export interface RateState { winStart: number; states: number; lastSaved: number }
export const STATE_PER_SEC = 20;
export const SAVED_MIN_GAP_MS = 1000;
export function allowMessage(prev: RateState | undefined, kind: CollabUp["t"], now: number): { ok: boolean; state: RateState } {
  const s: RateState = prev && now - prev.winStart < 1000 ? { ...prev } : { winStart: now, states: 0, lastSaved: prev?.lastSaved ?? 0 };
  if (kind === "saved") {
    if (now - s.lastSaved < SAVED_MIN_GAP_MS) return { ok: false, state: s };
    return { ok: true, state: { ...s, lastSaved: now } };
  }
  if (s.states >= STATE_PER_SEC) return { ok: false, state: s };
  return { ok: true, state: { ...s, states: s.states + 1 } };
}
